import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Role, RoleModel, RoleType, UiRoleType } from '../../../../services/RoleModel';
import { AraGenericChipComponent } from '@shared/ui/ara-generic-chip/ara-generic-chip.component';
import { AraPopoverComponent } from '@shared/ui/popover/popover.component';
import { RolesDisplayService } from '../roles-display.service';
import { buildBranchTimeline, TimelineSegment } from '../roles-view-model';

interface TimelineLaneView {
	main: boolean;
	label: string;
	segments: TimelineSegment[];
}

interface LineView {
	x1: number;
	y1: number;
	x2: number;
	y2: number;
}

interface SegmentCardView {
	key: string;
	left: number;
	top: number;
	width: number;
	isOpen: boolean;
	title: string;
	tooltip: string;
	role: Role;
	roleModel: RoleModel;
}

interface TimelineView {
	width: number;
	height: number;
	connectors: LineView[];
	dots: Array<{ x: number; y: number }>;
	cards: SegmentCardView[];
}

/** Vorab berechnete Geometrie des Belegungs-Strangs, an den die freien Rollen angebunden werden. */
interface TimelineLayout {
	mainLaneIndex: number;
	occupancyTop: number;
	occupancyBottom: number;
}

/**
 * Ereignisbasierter, horizontaler Zeitstrahl. Der Belegungs-Strang („Nutzer / Leerstand") ist eine
 * durchgehende, lückenlos aneinandergehängte Kartenreihe; freie Rollen liegen als Karten darüber und
 * sind über Verbindungslinien (Beginn/Ende) mit dem Strang verknüpft. Spalten = Übergangs-Grenzen.
 */
@Component({
	selector: 'ara-roles-branch-timeline',
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: './roles-branch-timeline.component.html',
	styleUrl: './roles-branch-timeline.component.scss',
	imports: [TranslocoDirective, AraGenericChipComponent, AraPopoverComponent],
})
export class RolesBranchTimelineComponent {
	protected readonly display = inject(RolesDisplayService);
	private readonly transloco = inject(TranslocoService);

	readonly roleModels = input<RoleModel[]>([]);
	readonly openPerson = output<RoleModel>();

	protected readonly rowHeight = 76;
	protected readonly cardHeight = 58;
	private readonly colWidth = 200;
	private readonly padLeft = 16;
	private readonly padRight = 24;

	protected readonly model = computed(() => buildBranchTimeline(this.roleModels()));

	// Reihenfolge oben→unten: Eigentümer · Nutzer/Leerstand (Hauptpfad) · übrige freie Rollen (abw. Empfänger …).
	protected readonly laneList = computed<TimelineLaneView[]>(() => {
		const built = this.model();
		const toLane = (roleType: RoleType, segments: TimelineSegment[]): TimelineLaneView => ({
			main: false,
			label: this.display.roleTypeLabel(roleType),
			segments,
		});
		const ownerLane = built.lanes.find((lane) => lane.roleType === UiRoleType.OWNER);
		const otherLanes = built.lanes.filter((lane) => lane.roleType !== UiRoleType.OWNER);
		const lanes: TimelineLaneView[] = [];
		if (ownerLane) {
			lanes.push(toLane(ownerLane.roleType, ownerLane.segments));
		}
		lanes.push({ main: true, label: this.transloco.translate('tenantunitDetails.editor.view.occupancy'), segments: built.occupancy });
		for (const lane of otherLanes) {
			lanes.push(toLane(lane.roleType, lane.segments));
		}
		return lanes;
	});

	protected readonly view = computed<TimelineView>(() => this.buildView());

	private xAt(index: number): number {
		return this.padLeft + index * this.colWidth;
	}

	private topOfLane(laneIndex: number): number {
		return laneIndex * this.rowHeight + (this.rowHeight - this.cardHeight) / 2;
	}

	private buildView(): TimelineView {
		const lanes = this.laneList();
		const events = this.model().events;
		if (events.length === 0) {
			return { width: 0, height: 0, connectors: [], dots: [], cards: [] };
		}

		const mainLaneIndex = lanes.findIndex((lane) => lane.main);
		const occupancyTop = this.topOfLane(mainLaneIndex);
		const layout: TimelineLayout = { mainLaneIndex, occupancyTop, occupancyBottom: occupancyTop + this.cardHeight };

		const connectors: LineView[] = [];
		const dots: Array<{ x: number; y: number }> = [];
		const cards: SegmentCardView[] = [];

		for (const [laneIndex, lane] of lanes.entries()) {
			const top = this.topOfLane(laneIndex);
			for (const [segIndex, segment] of lane.segments.entries()) {
				cards.push(this.buildCard(segment, lane.main, laneIndex, segIndex, top));
				if (lane.main) {
					continue;
				}
				const branch = this.branchConnectors(segment, laneIndex, top, layout);
				connectors.push(...branch.lines);
				dots.push(...branch.dots);
			}
		}

		return {
			width: this.padLeft + (events.length - 1) * this.colWidth + this.padRight,
			height: lanes.length * this.rowHeight,
			connectors,
			dots,
			cards,
		};
	}

	/** Karte eines Segments — Position, Maße und Beschriftung. */
	private buildCard(segment: TimelineSegment, main: boolean, laneIndex: number, segIndex: number, top: number): SegmentCardView {
		const left = this.xAt(segment.startIndex);
		const title = this.segmentTitle(segment, main);
		return {
			key: segment.role.id ?? `${segment.role.roleType}-${segment.startIndex}-${laneIndex}-${segIndex}`,
			left,
			top,
			width: this.xAt(segment.endIndex) - left,
			isOpen: segment.isOpen,
			title,
			tooltip: `${title} — ${this.display.periodLabel(segment.role)}`,
			role: segment.role,
			roleModel: segment.roleModel,
		};
	}

	/** Verbindung einer freien Rolle zum Belegungs-Strang (oberhalb → Oberkante, unterhalb → Unterkante); Ende nur bei abgeschlossenen Segmenten. */
	private branchConnectors(
		segment: TimelineSegment,
		laneIndex: number,
		top: number,
		layout: TimelineLayout
	): { lines: LineView[]; dots: Array<{ x: number; y: number }> } {
		const above = laneIndex < layout.mainLaneIndex;
		const anchorY = above ? layout.occupancyTop : layout.occupancyBottom;
		const branchEdgeY = above ? top + this.cardHeight : top;
		const startX = this.xAt(segment.startIndex);
		const lines: LineView[] = [{ x1: startX, y1: branchEdgeY, x2: startX, y2: anchorY }];
		const dots: Array<{ x: number; y: number }> = [{ x: startX, y: anchorY }];
		if (!segment.isOpen) {
			const endX = this.xAt(segment.endIndex);
			lines.push({ x1: endX, y1: branchEdgeY, x2: endX, y2: anchorY });
			dots.push({ x: endX, y: anchorY });
		}
		return { lines, dots };
	}

	/** Hauptpfad zeigt Rolle + Person; bei Leerstand (Name == Rollenlabel) nicht doppeln. */
	private segmentTitle(segment: TimelineSegment, main: boolean): string {
		const roleLabel = this.display.roleTypeLabel(segment.role.roleType);
		const personName = this.display.personName(segment.roleModel);
		if (!main) {
			return personName;
		}
		return personName && personName !== roleLabel ? `${roleLabel} · ${personName}` : roleLabel;
	}

	protected emitOpen(card: SegmentCardView): void {
		this.openPerson.emit(card.roleModel);
	}
}
